import React, { useState } from 'react';
import { Table, Alert, Badge, Card, Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx-js-style';

// ─── Types ─────────────────────────────────────────────────────────────────

interface TopTransaction {
  date: string;
  description: string;
  amount: number;
  direction: 'credit' | 'debit';
  account?: string;
  entity_name?: string;
  entity_type?: string;
  significance?: string;
  flag: 'suspicious' | 'notable' | 'normal';
}

interface SuspiciousPattern {
  pattern_type: string;
  title: string;
  description: string;
  total_amount: number;
  transaction_count: number;
  risk_level: 'high' | 'medium' | 'low';
  transaction_ids?: string[];
  recommendation?: string;
}

interface AccountSummary {
  account: string;
  bank: string;
  narrative: string;
  total_inflows: number;
  total_outflows: number;
  risk_level: 'high' | 'medium' | 'low';
  risk_notes?: string;
}

interface UnidentifiedTransfer {
  date: string;
  description: string;
  amount: number;
  direction: 'credit' | 'debit';
  account?: string;
  investigation_note?: string;
}

interface EntitySummary {
  entity_name: string;
  entity_type: string;
  total_received: number;
  total_paid: number;
  transaction_count: number;
  notes?: string;
}

interface CashActivityResults {
  executive_summary?: string;
  period?: { from: string; to: string };
  total_inflows?: number;
  total_outflows?: number;
  net_cash_flow?: number;
  transaction_count?: number;
  account_count?: number;
  account_summaries?: AccountSummary[];
  top_transactions?: TopTransaction[];
  suspicious_patterns?: SuspiciousPattern[];
  entity_summary?: EntitySummary[];
  unidentified_transfers?: UnidentifiedTransfer[];
  recommendations?: string[];
  _meta?: {
    statement_count: number;
    transaction_count: number;
    computed_inflows: number;
    computed_outflows: number;
    account_count: number;
    case_type: string;
    case_name: string;
    generated_at: string;
  };
}

interface CashActivityReportResultsProps {
  results: CashActivityResults;
  analysisName?: string;
  caseNumber?: string;
  caseTitle?: string;
  caseId?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n: number | undefined) =>
  (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const riskBadge: Record<string, string> = { high: 'danger', medium: 'warning', low: 'info' };

const flagColor: Record<string, string> = {
  suspicious: '#c0392b',
  notable: '#d68910',
  normal: '#212529',
};

const patternTypeLabel: Record<string, string> = {
  tax_prepayment:     'Tax Prepayment',
  structuring:        'Structuring',
  round_number:       'Round Dollar',
  related_party:      'Related Party',
  unusual_frequency:  'Unusual Frequency',
  lifestyle:          'Lifestyle Inflation',
  unexplained_inflow: 'Unexplained Inflow',
  threshold_avoidance:'Threshold Avoidance',
  unknown_transfer:   'Unknown Transfer',
  other:              'Other',
};

// ─── Component ──────────────────────────────────────────────────────────────

const CashActivityReportResults: React.FC<CashActivityReportResultsProps> = ({
  results,
  analysisName,
  caseNumber,
  caseTitle,
  caseId,
}) => {
  const [activeSection, setActiveSection] = useState<'accounts' | 'top' | 'patterns' | 'entities' | 'unidentified'>('accounts');

  // Always use Python-computed totals (authoritative) over LLM-reported values
  const computedInflows  = results._meta?.computed_inflows  ?? results.total_inflows  ?? 0;
  const computedOutflows = results._meta?.computed_outflows ?? results.total_outflows ?? 0;
  const net = computedInflows - computedOutflows;
  const suspiciousCount = (results.suspicious_patterns ?? []).filter(
    p => p.risk_level === 'high'
  ).length;

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const meta = results._meta;
    const expInflows  = meta?.computed_inflows  ?? results.total_inflows  ?? 0;
    const expOutflows = meta?.computed_outflows ?? results.total_outflows ?? 0;
    const expNet      = expInflows - expOutflows;

    // ── Style helpers ──────────────────────────────────────────────────────
    type CellStyle = Record<string, any>;
    const c = (v: any, s: CellStyle = {}, t?: string) => {
      const obj: any = { v, s };
      if (t) obj.t = t;
      else if (typeof v === 'number') obj.t = 'n';
      else obj.t = 's';
      return obj;
    };

    const DARK   = '1F2937';
    const MID    = '374151';
    const LIGHT  = '4B5563';
    const ALT    = 'F9FAFB';
    const GREEN  = '166534';
    const RED    = '991B1B';
    const WARN   = '92400E';
    const GREEN_BG = 'DCFCE7';
    const RED_BG   = 'FEE2E2';
    const WARN_BG  = 'FEF3C7';

    const titleStyle: CellStyle = { font: { bold: true, sz: 16, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: DARK } }, alignment: { horizontal: 'left', vertical: 'center' } };
    const sectionStyle: CellStyle = { font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: MID } }, alignment: { horizontal: 'left' } };
    const colHdrStyle: CellStyle = { font: { bold: true, sz: 10, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: LIGHT } }, alignment: { horizontal: 'center' }, border: { bottom: { style: 'thin', color: { rgb: 'CCCCCC' } } } };
    const labelStyle: CellStyle  = { font: { bold: true, sz: 10 }, alignment: { horizontal: 'left' } };
    const cur: CellStyle         = { numFmt: '"$"#,##0.00', alignment: { horizontal: 'right' } };
    const curGreen: CellStyle    = { ...cur, font: { color: { rgb: GREEN } } };
    const curRed: CellStyle      = { ...cur, font: { color: { rgb: RED } } };
    const curBold: CellStyle     = { ...cur, font: { bold: true } };
    const wrapStyle: CellStyle   = { font: { sz: 10 }, alignment: { wrapText: true, vertical: 'top' } };
    const mutedStyle: CellStyle  = { font: { sz: 9, color: { rgb: '6B7280' } } };
    const altRow = (i: number): CellStyle => i % 2 === 1 ? { fill: { fgColor: { rgb: ALT } } } : {};

    // ── Summary sheet ──────────────────────────────────────────────────────
    const summaryRows: any[][] = [
      [c('CASH ACTIVITY REPORT', titleStyle), '', '', '', ''],
      [c(`${caseNumber ?? ''} – ${caseTitle ?? ''}`, { font: { bold: true, sz: 12 } }), '', '', '', ''],
      [c(`Analysis: ${analysisName ?? ''}`, mutedStyle), '', '', '', ''],
      [c(`Period: ${results.period?.from ?? ''} to ${results.period?.to ?? ''}`, { font: { sz: 10 } }), '', '', '', ''],
      [c(`Generated: ${meta?.generated_at ? new Date(meta.generated_at).toLocaleString() : ''}`, mutedStyle), '', '', '', ''],
      ['', '', '', '', ''],
      [c('FINANCIAL SUMMARY', sectionStyle), '', '', '', ''],
      [c('', colHdrStyle), c('Amount', colHdrStyle), c('Count', colHdrStyle), '', ''],
      [c('Total Inflows',  labelStyle), c(expInflows,  curGreen, 'n'), c(meta?.transaction_count ?? 0, {}, 'n'), '', ''],
      [c('Total Outflows', labelStyle), c(expOutflows, curRed,   'n'), '', '', ''],
      [c('Net Cash Flow',  { ...labelStyle, font: { bold: true, sz: 11 } }), c(expNet, expNet >= 0 ? { ...cur, font: { bold: true, color: { rgb: '1D4ED8' } } } : { ...cur, font: { bold: true, color: { rgb: WARN } } }, 'n'), '', '', ''],
      ['', '', '', '', ''],
    ];

    if (results.account_summaries?.length) {
      summaryRows.push([c('ACCOUNT OVERVIEW', sectionStyle), '', '', '', '']);
      summaryRows.push([c('Account', colHdrStyle), c('Bank', colHdrStyle), c('Risk', colHdrStyle), c('Total Inflows', colHdrStyle), c('Total Outflows', colHdrStyle)]);
      results.account_summaries.forEach((a, i) => {
        const rs = altRow(i);
        const riskCol = a.risk_level === 'high' ? RED : a.risk_level === 'medium' ? WARN : GREEN;
        summaryRows.push([
          c(a.account, { ...rs, font: { bold: true } }),
          c(a.bank, rs),
          c(a.risk_level?.toUpperCase() ?? '', { ...rs, font: { bold: true, color: { rgb: riskCol } } }),
          c(a.total_inflows ?? 0, { ...curGreen, fill: rs.fill }, 'n'),
          c(a.total_outflows ?? 0, { ...curRed, fill: rs.fill }, 'n'),
        ]);
      });
      summaryRows.push(['', '', '', '', '']);
    }

    summaryRows.push([c('EXECUTIVE SUMMARY', sectionStyle), '', '', '', '']);
    results.executive_summary?.split('\n').filter(Boolean).forEach(para => {
      summaryRows.push([c(para, wrapStyle), '', '', '', '']);
    });

    if (results.recommendations?.length) {
      summaryRows.push(['', '', '', '', '']);
      summaryRows.push([c('INVESTIGATIVE RECOMMENDATIONS', sectionStyle), '', '', '', '']);
      results.recommendations.forEach((r, i) => {
        summaryRows.push([c(`${i + 1}. ${r}`, wrapStyle), '', '', '', '']);
      });
    }

    const summaryWS = XLSX.utils.aoa_to_sheet(summaryRows);
    summaryWS['!cols'] = [{ wch: 45 }, { wch: 22 }, { wch: 12 }, { wch: 18 }, { wch: 18 }];
    summaryWS['!rows'] = [{ hpt: 40 }];
    XLSX.utils.book_append_sheet(wb, summaryWS, 'Summary');

    // ── Account Narratives sheet ───────────────────────────────────────────
    if (results.account_summaries?.length) {
      const acctRows: any[][] = [
        [c('ACCOUNT NARRATIVES', titleStyle), '', ''],
      ];
      results.account_summaries.forEach(a => {
        const riskBg = a.risk_level === 'high' ? RED_BG : a.risk_level === 'medium' ? WARN_BG : GREEN_BG;
        acctRows.push(['', '', '']);
        acctRows.push([c(`${a.bank ? `${a.bank} — ` : ''}Account ${a.account}`, sectionStyle), '', '']);
        acctRows.push([c('Risk Level', labelStyle), c(a.risk_level?.toUpperCase() ?? '', { font: { bold: true, color: { rgb: a.risk_level === 'high' ? RED : a.risk_level === 'medium' ? WARN : GREEN } }, fill: { fgColor: { rgb: riskBg } } }), '']);
        acctRows.push([c('Total Inflows',  labelStyle), c(a.total_inflows  ?? 0, curGreen, 'n'), '']);
        acctRows.push([c('Total Outflows', labelStyle), c(a.total_outflows ?? 0, curRed,   'n'), '']);
        acctRows.push(['', '', '']);
        a.narrative?.split('\n').filter(Boolean).forEach(para => {
          acctRows.push([c(para, wrapStyle), '', '']);
        });
        if (a.risk_notes) {
          acctRows.push(['', '', '']);
          acctRows.push([c('Risk Assessment:', labelStyle), c(a.risk_notes, wrapStyle), '']);
        }
      });
      const acctWS = XLSX.utils.aoa_to_sheet(acctRows);
      acctWS['!cols'] = [{ wch: 20 }, { wch: 90 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, acctWS, 'Account Narratives');
    }

    // ── Top Transactions sheet ─────────────────────────────────────────────
    if (results.top_transactions?.length) {
      const txRows: any[][] = [
        [c('TOP TRANSACTIONS BY AMOUNT', titleStyle), '', '', '', '', '', ''],
        [c('Date', colHdrStyle), c('Direction', colHdrStyle), c('Amount', colHdrStyle), c('Description', colHdrStyle), c('Entity', colHdrStyle), c('Account', colHdrStyle), c('Significance', colHdrStyle)],
      ];
      results.top_transactions.forEach((t, i) => {
        const rowBg = t.flag === 'suspicious' ? RED_BG : t.flag === 'notable' ? WARN_BG : (i % 2 === 1 ? ALT : 'FFFFFF');
        const fill = { fill: { fgColor: { rgb: rowBg } } };
        const amtColor = t.direction === 'credit' ? GREEN : RED;
        txRows.push([
          c(t.date, fill),
          c(t.direction === 'credit' ? 'CREDIT ↑' : 'DEBIT ↓', { ...fill, font: { color: { rgb: amtColor } } }),
          c(t.amount, { ...curBold, ...fill, font: { bold: true, color: { rgb: amtColor } } }, 'n'),
          c(t.description, { ...fill, alignment: { wrapText: true } }),
          c(t.entity_name ?? '', fill),
          c(t.account ?? '', { ...fill, font: { sz: 9, color: { rgb: '6B7280' } } }),
          c(t.significance ?? '', { ...fill, font: { sz: 9 } }),
        ]);
      });
      const txWS = XLSX.utils.aoa_to_sheet(txRows);
      txWS['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 48 }, { wch: 28 }, { wch: 12 }, { wch: 32 }];
      txWS['!rows'] = [{ hpt: 36 }];
      XLSX.utils.book_append_sheet(wb, txWS, 'Top Transactions');
    }

    // ── Suspicious Patterns sheet ──────────────────────────────────────────
    if (results.suspicious_patterns?.length) {
      const patRows: any[][] = [
        [c('SUSPICIOUS PATTERNS & RED FLAGS', titleStyle), '', '', ''],
        [c('Pattern / Title', colHdrStyle), c('Risk', colHdrStyle), c('Total Amount', colHdrStyle), c('Description & Recommendation', colHdrStyle)],
      ];
      results.suspicious_patterns.forEach((p, i) => {
        const rowBg = p.risk_level === 'high' ? RED_BG : p.risk_level === 'medium' ? WARN_BG : (i % 2 === 1 ? ALT : 'FFFFFF');
        const fill = { fill: { fgColor: { rgb: rowBg } } };
        const riskColor = p.risk_level === 'high' ? RED : p.risk_level === 'medium' ? WARN : GREEN;
        patRows.push([
          c(`${patternTypeLabel[p.pattern_type] ?? p.pattern_type}\n${p.title}`, { ...fill, font: { bold: true }, alignment: { wrapText: true, vertical: 'top' } }),
          c(p.risk_level?.toUpperCase() ?? '', { ...fill, font: { bold: true, color: { rgb: riskColor } } }),
          c(p.total_amount ?? 0, { ...curBold, ...fill }, 'n'),
          c(`${p.description}${p.recommendation ? `\n\nRECOMMENDATION: ${p.recommendation}` : ''}`, { ...fill, alignment: { wrapText: true, vertical: 'top' } }),
        ]);
      });
      const patWS = XLSX.utils.aoa_to_sheet(patRows);
      patWS['!cols'] = [{ wch: 28 }, { wch: 10 }, { wch: 16 }, { wch: 72 }];
      XLSX.utils.book_append_sheet(wb, patWS, 'Suspicious Patterns');
    }

    // ── Unidentified Transfers sheet ───────────────────────────────────────
    if (results.unidentified_transfers?.length) {
      const utRows: any[][] = [
        [c('UNIDENTIFIED TRANSFERS — REQUIRE INVESTIGATION', titleStyle), '', '', '', ''],
        [c('Date', colHdrStyle), c('Direction', colHdrStyle), c('Amount', colHdrStyle), c('Description', colHdrStyle), c('Account', colHdrStyle), c('Investigation Note', colHdrStyle)],
      ];
      results.unidentified_transfers.forEach((t, i) => {
        const fill = { fill: { fgColor: { rgb: i % 2 === 0 ? 'FFFBEB' : ALT } } };
        const amtColor = t.direction === 'credit' ? GREEN : RED;
        utRows.push([
          c(t.date, fill),
          c(t.direction === 'credit' ? 'CREDIT ↑' : 'DEBIT ↓', { ...fill, font: { color: { rgb: amtColor } } }),
          c(t.amount, { ...curBold, ...fill, font: { bold: true, color: { rgb: amtColor } } }, 'n'),
          c(t.description, fill),
          c(t.account ?? '', { ...fill, font: { sz: 9, color: { rgb: '6B7280' } } }),
          c(t.investigation_note ?? '', { ...fill, font: { sz: 9 }, alignment: { wrapText: true, vertical: 'top' } }),
        ]);
      });
      const utWS = XLSX.utils.aoa_to_sheet(utRows);
      utWS['!cols'] = [{ wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 48 }, { wch: 12 }, { wch: 52 }];
      XLSX.utils.book_append_sheet(wb, utWS, 'Unidentified Transfers');
    }

    // ── Entity Summary sheet ───────────────────────────────────────────────
    if (results.entity_summary?.length) {
      const entRows: any[][] = [
        [c('ENTITY SUMMARY', titleStyle), '', '', '', '', ''],
        [c('Entity Name', colHdrStyle), c('Type', colHdrStyle), c('Total Received', colHdrStyle), c('Total Paid', colHdrStyle), c('Transactions', colHdrStyle), c('Forensic Notes', colHdrStyle)],
      ];
      results.entity_summary.forEach((e, i) => {
        const fill = altRow(i);
        entRows.push([
          c(e.entity_name, { ...fill, font: { bold: true } }),
          c(e.entity_type, fill),
          e.total_received > 0 ? c(e.total_received, { ...curGreen, fill: fill.fill }, 'n') : c('', fill),
          e.total_paid > 0     ? c(e.total_paid,     { ...curRed,   fill: fill.fill }, 'n') : c('', fill),
          c(e.transaction_count, fill, 'n'),
          c(e.notes ?? '', { ...fill, font: { sz: 9 }, alignment: { wrapText: true, vertical: 'top' } }),
        ]);
      });
      const entWS = XLSX.utils.aoa_to_sheet(entRows);
      entWS['!cols'] = [{ wch: 32 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 55 }];
      XLSX.utils.book_append_sheet(wb, entWS, 'Entity Summary');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `cash-activity-report-${caseNumber ?? caseId}.xlsx`);
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">{analysisName ?? 'Cash Activity Report'}</h4>
          {(caseNumber || caseTitle) && (
            <small className="text-muted">{caseNumber} – {caseTitle}</small>
          )}
          {results.period && (
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              Period: {results.period.from} → {results.period.to}
            </div>
          )}
        </div>
        <Button variant="outline-success" size="sm" onClick={handleExport}>
          Export to Excel
        </Button>
      </div>

      {/* KPI cards */}
      <Row className="g-2 mb-3">
        <Col xs={6} md={3}>
          <Card className="h-100 border-success">
            <Card.Body className="py-2 px-3">
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Total Inflows</div>
              <div style={{ fontWeight: 600, color: '#27ae60', fontSize: '1rem' }}>
                {fmt(computedInflows)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className="h-100 border-danger">
            <Card.Body className="py-2 px-3">
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Total Outflows</div>
              <div style={{ fontWeight: 600, color: '#c0392b', fontSize: '1rem' }}>
                {fmt(computedOutflows)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className={`h-100 border-${net >= 0 ? 'primary' : 'warning'}`}>
            <Card.Body className="py-2 px-3">
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>Net Cash Flow</div>
              <div style={{ fontWeight: 600, color: net >= 0 ? '#2980b9' : '#e67e22', fontSize: '1rem' }}>
                {fmt(net)}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card className={`h-100 ${suspiciousCount > 0 ? 'border-danger' : 'border-secondary'}`}>
            <Card.Body className="py-2 px-3">
              <div className="text-muted" style={{ fontSize: '0.75rem' }}>High-Risk Patterns</div>
              <div style={{ fontWeight: 600, color: suspiciousCount > 0 ? '#c0392b' : '#6c757d', fontSize: '1rem' }}>
                {suspiciousCount}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Executive Summary */}
      {results.executive_summary && (
        <Card className="mb-3 border-dark">
          <Card.Header className="py-2 fw-semibold bg-dark text-white">Forensic Summary</Card.Header>
          <Card.Body>
            {results.executive_summary.split('\n').filter(Boolean).map((para, i) => (
              <p key={i} className="mb-2">{para}</p>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* Recommendations */}
      {results.recommendations && results.recommendations.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <strong>Investigative Recommendations</strong>
          <ol className="mt-2 mb-0 ps-3">
            {results.recommendations.map((r, i) => (
              <li key={i} className="mb-1">{r}</li>
            ))}
          </ol>
        </Alert>
      )}

      {/* Section tabs */}
      <ButtonGroup className="mb-3 flex-wrap" size="sm">
        <Button
          variant={activeSection === 'accounts' ? 'dark' : 'outline-dark'}
          onClick={() => setActiveSection('accounts')}
        >
          Account Narratives ({results.account_summaries?.length ?? 0})
        </Button>
        <Button
          variant={activeSection === 'top' ? 'secondary' : 'outline-secondary'}
          onClick={() => setActiveSection('top')}
        >
          Top Transactions ({results.top_transactions?.length ?? 0})
        </Button>
        <Button
          variant={activeSection === 'patterns' ? 'danger' : 'outline-danger'}
          onClick={() => setActiveSection('patterns')}
        >
          Suspicious Patterns ({results.suspicious_patterns?.length ?? 0})
        </Button>
        <Button
          variant={activeSection === 'unidentified' ? 'warning' : 'outline-warning'}
          onClick={() => setActiveSection('unidentified')}
        >
          Unidentified Transfers ({results.unidentified_transfers?.length ?? 0})
        </Button>
        <Button
          variant={activeSection === 'entities' ? 'primary' : 'outline-primary'}
          onClick={() => setActiveSection('entities')}
        >
          Entities ({results.entity_summary?.length ?? 0})
        </Button>
      </ButtonGroup>

      {/* Account Narratives */}
      {activeSection === 'accounts' && (
        <div>
          {!results.account_summaries?.length ? (
            <Alert variant="secondary">No account narrative data available.</Alert>
          ) : (
            results.account_summaries.map((a, i) => (
              <Card key={i} className={`mb-3 border-${riskBadge[a.risk_level] ?? 'secondary'}`}>
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                  <span>
                    <Badge bg={riskBadge[a.risk_level] ?? 'secondary'} className="me-2">
                      {a.risk_level?.toUpperCase()}
                    </Badge>
                    <strong>{a.bank ? `${a.bank} — ` : ''}{a.account}</strong>
                  </span>
                  <span className="d-flex gap-3" style={{ fontSize: '0.82rem' }}>
                    <span style={{ color: '#27ae60' }}>In: {fmt(a.total_inflows)}</span>
                    <span style={{ color: '#c0392b' }}>Out: {fmt(a.total_outflows)}</span>
                  </span>
                </Card.Header>
                <Card.Body className="py-3">
                  {a.narrative?.split('\n').filter(Boolean).map((para, j) => (
                    <p key={j} className="mb-2" style={{ fontSize: '0.9rem', lineHeight: 1.65 }}>{para}</p>
                  ))}
                  {a.risk_notes && (
                    <Alert variant={a.risk_level === 'high' ? 'danger' : a.risk_level === 'medium' ? 'warning' : 'info'}
                      className="mb-0 mt-2 py-2" style={{ fontSize: '0.82rem' }}>
                      <strong>Risk assessment:</strong> {a.risk_notes}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Top Transactions */}
      {activeSection === 'top' && (
        <div style={{ overflowX: 'auto' }}>
          {!results.top_transactions?.length ? (
            <Alert variant="secondary">No transactions available.</Alert>
          ) : (
            <Table size="sm" hover bordered style={{ fontSize: '0.82rem', minWidth: 700 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#212529', color: '#fff' }}>
                <tr>
                  <th>Date</th>
                  <th>Dir</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Description</th>
                  <th>Entity</th>
                  <th>Account</th>
                  <th>Significance</th>
                </tr>
              </thead>
              <tbody>
                {results.top_transactions.map((t, i) => (
                  <tr
                    key={i}
                    style={{
                      background: t.flag === 'suspicious' ? '#fdf0ed' : t.flag === 'notable' ? '#fefae8' : undefined,
                    }}
                  >
                    <td style={{ whiteSpace: 'nowrap' }}>{t.date}</td>
                    <td>
                      <Badge bg={t.direction === 'credit' ? 'success' : 'danger'}>
                        {t.direction === 'credit' ? '↑' : '↓'}
                      </Badge>
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        fontWeight: 600,
                        color: flagColor[t.flag],
                      }}
                    >
                      {fmt(t.amount)}
                    </td>
                    <td style={{ maxWidth: 220 }}>{t.description}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {t.entity_name && (
                        <span>
                          {t.entity_name}
                          {t.entity_type && (
                            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>
                              ({t.entity_type})
                            </span>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#6c757d' }}>
                      {t.account}
                    </td>
                    <td style={{ color: flagColor[t.flag], fontSize: '0.78rem' }}>
                      {t.flag !== 'normal' && (
                        <Badge bg={t.flag === 'suspicious' ? 'danger' : 'warning'} className="me-1">
                          {t.flag}
                        </Badge>
                      )}
                      {t.significance}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* Suspicious Patterns */}
      {activeSection === 'patterns' && (
        <div>
          {!results.suspicious_patterns?.length ? (
            <Alert variant="success">No suspicious patterns identified.</Alert>
          ) : (
            results.suspicious_patterns.map((p, i) => (
              <Card key={i} className={`mb-2 border-${riskBadge[p.risk_level] ?? 'secondary'}`}>
                <Card.Header className="d-flex justify-content-between align-items-center py-2">
                  <span>
                    <Badge bg={riskBadge[p.risk_level] ?? 'secondary'} className="me-2">
                      {p.risk_level.toUpperCase()}
                    </Badge>
                    <strong>{p.title}</strong>
                    <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>
                      {patternTypeLabel[p.pattern_type] ?? p.pattern_type}
                    </span>
                  </span>
                  <span className="text-muted" style={{ fontSize: '0.82rem' }}>
                    {p.transaction_count} txns · {fmt(p.total_amount)}
                  </span>
                </Card.Header>
                <Card.Body className="py-2">
                  <p className="mb-1" style={{ fontSize: '0.88rem' }}>{p.description}</p>
                  {p.recommendation && (
                    <Alert variant="warning" className="mb-0 mt-2 py-1 px-2" style={{ fontSize: '0.8rem' }}>
                      <strong>Recommended action:</strong> {p.recommendation}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Unidentified Transfers */}
      {activeSection === 'unidentified' && (
        <div style={{ overflowX: 'auto' }}>
          {!results.unidentified_transfers?.length ? (
            <Alert variant="success">No unidentified transfers flagged.</Alert>
          ) : (
            <>
              <Alert variant="warning" className="py-2" style={{ fontSize: '0.85rem' }}>
                These transactions involve counterparties that could not be identified from available records.
                Supporting documentation should be obtained to trace the source or destination of funds.
              </Alert>
              <Table size="sm" hover bordered style={{ fontSize: '0.82rem', minWidth: 650 }}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff3cd' }}>
                  <tr>
                    <th>Date</th>
                    <th>Dir</th>
                    <th style={{ textAlign: 'right' }}>Amount</th>
                    <th>Description</th>
                    <th>Account</th>
                    <th>Investigation Note</th>
                  </tr>
                </thead>
                <tbody>
                  {results.unidentified_transfers.map((t, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? '#fffdf0' : '#fff' }}>
                      <td style={{ whiteSpace: 'nowrap' }}>{t.date}</td>
                      <td>
                        <Badge bg={t.direction === 'credit' ? 'success' : 'danger'}>
                          {t.direction === 'credit' ? '↑' : '↓'}
                        </Badge>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: 600,
                        color: t.direction === 'debit' ? '#c0392b' : '#27ae60' }}>
                        {fmt(t.amount)}
                      </td>
                      <td>{t.description}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.75rem', color: '#6c757d' }}>{t.account}</td>
                      <td style={{ fontSize: '0.78rem', color: '#6c757d' }}>{t.investigation_note}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      )}

      {/* Entity Summary */}
      {activeSection === 'entities' && (
        <div style={{ overflowX: 'auto' }}>
          {!results.entity_summary?.length ? (
            <Alert variant="secondary">No entity data available.</Alert>
          ) : (
            <Table size="sm" hover bordered style={{ fontSize: '0.82rem', minWidth: 600 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa' }}>
                <tr>
                  <th>Entity</th>
                  <th>Type</th>
                  <th style={{ textAlign: 'right' }}>Received</th>
                  <th style={{ textAlign: 'right' }}>Paid</th>
                  <th>Txns</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {results.entity_summary.map((e, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9fa' }}>
                    <td style={{ fontWeight: 500 }}>{e.entity_name}</td>
                    <td>
                      <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>{e.entity_type}</Badge>
                    </td>
                    <td style={{ textAlign: 'right', color: '#27ae60', whiteSpace: 'nowrap' }}>
                      {e.total_received > 0 ? fmt(e.total_received) : '—'}
                    </td>
                    <td style={{ textAlign: 'right', color: '#c0392b', whiteSpace: 'nowrap' }}>
                      {e.total_paid > 0 ? fmt(e.total_paid) : '—'}
                    </td>
                    <td>{e.transaction_count}</td>
                    <td style={{ fontSize: '0.78rem', color: '#555' }}>{e.notes}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* Meta footer */}
      {results._meta && (
        <div className="text-muted mt-3" style={{ fontSize: '0.75rem' }}>
          Generated from {results._meta.statement_count} statements ·{' '}
          {results._meta.transaction_count} transactions analyzed ·{' '}
          {results._meta.generated_at ? new Date(results._meta.generated_at).toLocaleString() : ''}
        </div>
      )}
    </div>
  );
};

export default CashActivityReportResults;
